import { useMemo } from 'react'

import { NextRouter, useRouter } from 'next/router'

import { DEFAULT_SEARCH_FILTER_KEY } from '@/utils/params'

export const ENFORCE_STRING_KEYS = [
  'identifierPrefix',
  DEFAULT_SEARCH_FILTER_KEY,
]

type Return<T extends Record<string, unknown>> = Partial<T> & {
  push: NextRouter['push']
  isReady: NextRouter['isReady']
}

/**
 * This is simply a typed wrapper around the `useRouter` hook from Next.js.
 * It returns the query params and the push function.
 * It is intended to be used mainly to get the pathname params. If you want to
 * get the actual query params, you should use the `useQueryParams` hook.
 */
export const useRouterParams = <T extends Record<string, unknown>>(
  defaults: Partial<T> = {}
): Return<T> => {
  const { query, push, isReady } = useRouter()

  const sanitisedQuery = useMemo<T>(() => {
    return Object.keys(query).reduce((acc, key) => {
      if (ENFORCE_STRING_KEYS.includes(key)) {
        return { ...acc, [key]: String(query[key]) }
      }

      // Check if the query param is a entirely a number. Using `Number` instead of `pasrseInt` because parseInt will return 123 for '123abc'
      // We also check if the first character is not '0' because we don't want to convert '007' to 7 for example
      if (!isNaN(Number(query[key])) && query[key][0] !== '0') {
        return { ...acc, [key]: Number(query[key]) }
      }

      if (query[key] === 'true') {
        return { ...acc, [key]: true }
      }

      if (query[key] === 'false') {
        return { ...acc, [key]: false }
      }

      return { ...acc, [key]: query[key] }
    }, defaults as T)
  }, [query, defaults])

  return { ...sanitisedQuery, push, isReady }
}
